"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
const errors_js_1 = require("./errors.js");
const native_js_1 = __importDefault(require("./native.js"));
const extra_js_1 = __importDefault(require("./extra.js"));
const hasOwn = Function.prototype.call.bind(Object.prototype.hasOwnProperty);
const TYPES = {
    arrayOf: function arrayOf(type, options) {
        const compiledType = compile(type);
        const opts = options || {};
        function _arrayOf(array, strict) {
            if (!native_js_1.default.Array(array))
                return false;
            if (native_js_1.default.Nil(array))
                return false;
            const arr = array;
            if (opts.minLength !== undefined && arr.length < opts.minLength)
                return false;
            if (opts.maxLength !== undefined && arr.length > opts.maxLength)
                return false;
            if (opts.length !== undefined && arr.length !== opts.length)
                return false;
            return arr.every(function (value, i) {
                try {
                    return typeforce(compiledType, value, strict);
                }
                catch (e) {
                    throw (0, errors_js_1.tfSubError)(e, i);
                }
            });
        }
        _arrayOf.toJSON = function () {
            let str = '[' + (0, errors_js_1.tfJSON)(compiledType) + ']';
            if (opts.length !== undefined) {
                str += '{' + opts.length + '}';
            }
            else if (opts.minLength !== undefined || opts.maxLength !== undefined) {
                str +=
                    '{' +
                        (opts.minLength === undefined ? 0 : opts.minLength) +
                        ',' +
                        (opts.maxLength === undefined ? Infinity : opts.maxLength) +
                        '}';
            }
            return str;
        };
        return _arrayOf;
    },
    maybe: function maybe(type) {
        const compiledType = compile(type);
        function _maybe(value, strict) {
            return native_js_1.default.Nil(value) || compiledType(value, strict, maybe);
        }
        _maybe.toJSON = function () {
            return '?' + (0, errors_js_1.tfJSON)(compiledType);
        };
        return _maybe;
    },
    map: function map(propertyType, propertyKeyType) {
        const compiledPropertyType = compile(propertyType);
        const compiledPropertyKeyType = propertyKeyType ? compile(propertyKeyType) : undefined;
        function _map(value, strict) {
            if (!native_js_1.default.Object(value))
                return false;
            if (native_js_1.default.Nil(value))
                return false;
            for (const propertyName in value) {
                try {
                    if (compiledPropertyKeyType) {
                        typeforce(compiledPropertyKeyType, propertyName, strict);
                    }
                }
                catch (e) {
                    throw (0, errors_js_1.tfSubError)(e, propertyName, 'key');
                }
                try {
                    const propertyValue = value[propertyName];
                    typeforce(compiledPropertyType, propertyValue, strict);
                }
                catch (e) {
                    throw (0, errors_js_1.tfSubError)(e, propertyName);
                }
            }
            return true;
        }
        if (compiledPropertyKeyType) {
            _map.toJSON = function () {
                return ('{' +
                    (0, errors_js_1.tfJSON)(compiledPropertyKeyType) +
                    ': ' +
                    (0, errors_js_1.tfJSON)(compiledPropertyType) +
                    '}');
            };
        }
        else {
            _map.toJSON = function () {
                return '{' + (0, errors_js_1.tfJSON)(compiledPropertyType) + '}';
            };
        }
        return _map;
    },
    object: function object(uncompiled) {
        const type = {};
        for (const typePropertyName in uncompiled) {
            type[typePropertyName] = compile(uncompiled[typePropertyName]);
        }
        function _object(value, strict) {
            if (!native_js_1.default.Object(value))
                return false;
            if (native_js_1.default.Nil(value))
                return false;
            let propertyName;
            try {
                for (propertyName in type) {
                    const propertyType = type[propertyName];
                    const propertyValue = value[propertyName];
                    typeforce(propertyType, propertyValue, strict);
                }
            }
            catch (e) {
                throw (0, errors_js_1.tfSubError)(e, propertyName);
            }
            if (strict) {
                for (propertyName in value) {
                    if (hasOwn(type, propertyName) && type[propertyName])
                        continue;
                    throw new errors_js_1.TfPropertyTypeError(undefined, propertyName);
                }
            }
            return true;
        }
        _object.toJSON = function () {
            return (0, errors_js_1.tfJSON)(type);
        };
        return _object;
    },
    anyOf: function anyOf(...args) {
        const types = args.map(compile);
        function _anyOf(value, strict) {
            return types.some(function (type) {
                try {
                    return typeforce(type, value, strict);
                }
                catch {
                    return false;
                }
            });
        }
        _anyOf.toJSON = function () {
            return types.map((t) => (0, errors_js_1.tfJSON)(t)).join('|');
        };
        return _anyOf;
    },
    allOf: function allOf(...args) {
        const types = args.map(compile);
        function _allOf(value, strict) {
            return types.every(function (type) {
                try {
                    return typeforce(type, value, strict);
                }
                catch {
                    return false;
                }
            });
        }
        _allOf.toJSON = function () {
            return types.map((t) => (0, errors_js_1.tfJSON)(t)).join(' & ');
        };
        return _allOf;
    },
    quacksLike: function quacksLike(type) {
        function _quacksLike(value) {
            return type === (0, errors_js_1.getValueTypeName)(value);
        }
        _quacksLike.toJSON = function () {
            return type;
        };
        return _quacksLike;
    },
    tuple: function tuple(...args) {
        const types = args.map(compile);
        function _tuple(values, strict) {
            if (native_js_1.default.Nil(values))
                return false;
            if (native_js_1.default.Nil(values.length))
                return false;
            const vals = values;
            if (strict && vals.length !== types.length)
                return false;
            return types.every(function (type, i) {
                try {
                    return typeforce(type, vals[i], strict);
                }
                catch (e) {
                    throw (0, errors_js_1.tfSubError)(e, i);
                }
            });
        }
        _tuple.toJSON = function () {
            return '(' + types.map((t) => (0, errors_js_1.tfJSON)(t)).join(', ') + ')';
        };
        return _tuple;
    },
    value: function value(expected) {
        function _value(actual) {
            return actual === expected;
        }
        _value.toJSON = function () {
            return expected;
        };
        return _value;
    },
    oneOf: undefined,
};
TYPES.oneOf = TYPES.anyOf;
function compile(type) {
    if (native_js_1.default.String(type)) {
        const str = type;
        if (str[0] === '?')
            return TYPES.maybe(str.slice(1));
        return (native_js_1.default[str] || TYPES.quacksLike(str));
    }
    if (type && native_js_1.default.Object(type)) {
        if (native_js_1.default.Array(type)) {
            const arr = type;
            if (arr.length !== 1) {
                throw new TypeError('Expected compile() parameter of type Array of length 1');
            }
            return TYPES.arrayOf(arr[0]);
        }
        return TYPES.object(type);
    }
    if (native_js_1.default.Function(type)) {
        return type;
    }
    return TYPES.value(type);
}
function typeforce(type, value, strict, surrogate) {
    if (native_js_1.default.Function(type)) {
        if (type(value, strict))
            return true;
        throw new errors_js_1.TfTypeError(surrogate || type, value);
    }
    return typeforce(compile(type), value, strict);
}
const tf = typeforce;
for (const typeName in native_js_1.default) {
    tf[typeName] = native_js_1.default[typeName];
}
for (const typeName in TYPES) {
    tf[typeName] = TYPES[typeName];
}
for (const typeName in extra_js_1.default) {
    tf[typeName] = extra_js_1.default[typeName];
}
tf.compile = compile;
tf.parse = (type, value, strict, surrogate) => {
    if (strict === false)
        throw new Error('parse() is always strict');
    if (typeforce(type, value, true, surrogate))
        return value;
    throw new Error('failed to parse');
};
tf.TfTypeError = errors_js_1.TfTypeError;
tf.TfPropertyTypeError = errors_js_1.TfPropertyTypeError;
module.exports = typeforce;
